import type { DiskArchiveEntry } from '../Contracts/DiskArchive'
import type { DiskFileKind } from '../Contracts/DiskNode'

/** Рядок переглядача архіву: файл або папка (явна чи неявна — префікс шляху). */
export interface DiskArchiveRow {
    /** Шлях усередині архіву — ключ рядка */
    key: string
    name: string
    /** Папка, в якій лежить (для плаского списку пошуку); у корені — порожньо */
    folder: string
    dir: boolean
    /** Файл — його розмір; папка — сума файлів усередині */
    size: number
    compressedSize: number
    modifiedAt: string | null
    encrypted: boolean
    /** Файлів усередині папки (на будь-якій глибині) */
    files: number
    /** Лише в папки з вмістом: antd малює стрілку розгортання й для порожнього масиву */
    children?: DiskArchiveRow[]
}

const collator = new Intl.Collator('uk', { numeric: true, sensitivity: 'base' })

const baseName = (path: string): string => path.slice(path.lastIndexOf('/') + 1)
const folderOf = (path: string): string => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '')

const rowOf = (entry: DiskArchiveEntry): DiskArchiveRow => ({
    key: entry.path,
    name: baseName(entry.path),
    folder: folderOf(entry.path),
    dir: entry.dir,
    size: entry.dir ? 0 : entry.size,
    compressedSize: entry.dir ? 0 : entry.compressedSize,
    modifiedAt: entry.modifiedAt,
    encrypted: entry.encrypted,
    files: entry.dir ? 0 : 1,
})

/** Папки зверху, далі природно: «Файл 2» перед «Файл 10» — як у списку диска. */
const byFolderThenName = (a: DiskArchiveRow, b: DiskArchiveRow): number =>
    a.dir === b.dir ? collator.compare(a.name, b.name) : a.dir ? -1 : 1

/**
 * Дерево з плаского переліку: неявні папки (архіватор не записав їх
 * окремо) створюються зі шляхів, розмір папки — сума файлів усередині.
 */
export const buildArchiveTree = (entries: readonly DiskArchiveEntry[]): DiskArchiveRow[] => {
    const nodes = new Map<string, DiskArchiveRow & { kids: Map<string, DiskArchiveRow> }>()
    const roots = new Map<string, DiskArchiveRow>()

    const folder = (path: string): DiskArchiveRow & { kids: Map<string, DiskArchiveRow> } => {
        const existing = nodes.get(path)
        if (existing) return existing

        const created = {
            ...rowOf({ path, dir: true, size: 0, compressedSize: 0, modifiedAt: null, encrypted: false }),
            kids: new Map<string, DiskArchiveRow>(),
        }
        nodes.set(path, created)

        const parent = folderOf(path)
        ;(parent === '' ? roots : folder(parent).kids).set(path, created)

        return created
    }

    for (const entry of entries) {
        if (entry.path === '') continue

        if (entry.dir) {
            const row = folder(entry.path)
            row.modifiedAt = entry.modifiedAt
            continue
        }

        const row = rowOf(entry)
        const parent = folderOf(entry.path)
        ;(parent === '' ? roots : folder(parent).kids).set(entry.path, row)
    }

    const finish = (rows: Iterable<DiskArchiveRow>): DiskArchiveRow[] =>
        [...rows]
            .map((row) => {
                const node = nodes.get(row.key)
                if (!row.dir || !node) return row

                const children = finish(node.kids.values())

                return {
                    key: node.key,
                    name: node.name,
                    folder: node.folder,
                    dir: true,
                    modifiedAt: node.modifiedAt,
                    encrypted: false,
                    size: children.reduce((sum, child) => sum + child.size, 0),
                    compressedSize: children.reduce((sum, child) => sum + child.compressedSize, 0),
                    files: children.reduce((sum, child) => sum + child.files, 0),
                    ...(children.length > 0 ? { children } : {}),
                }
            })
            .sort(byFolderThenName)

    return finish(roots.values())
}

/**
 * Пошук за назвою запису (без урахування регістру, кирилиця — теж): пласкі
 * рядки з папкою, де запис лежить. Порожній запит — порожньо (показують дерево).
 */
export const searchArchive = (entries: readonly DiskArchiveEntry[], query: string): DiskArchiveRow[] => {
    const needle = query.trim().toLocaleLowerCase('uk')
    if (needle === '') return []

    return entries
        .filter((entry) => baseName(entry.path).toLocaleLowerCase('uk').includes(needle))
        .map(rowOf)
        .sort((a, b) => byFolderThenName(a, b) || collator.compare(a.folder, b.folder))
}

const KIND_BY_EXTENSION: Record<string, DiskFileKind> = {
    pdf: 'pdf',
    jpg: 'image',
    jpeg: 'image',
    png: 'image',
    gif: 'image',
    webp: 'image',
    bmp: 'image',
    tif: 'image',
    tiff: 'image',
    svg: 'image',
    heic: 'image',
    doc: 'document',
    docx: 'document',
    odt: 'document',
    rtf: 'document',
    xls: 'spreadsheet',
    xlsx: 'spreadsheet',
    ods: 'spreadsheet',
    csv: 'spreadsheet',
    ppt: 'presentation',
    pptx: 'presentation',
    odp: 'presentation',
    zip: 'archive',
    rar: 'archive',
    '7z': 'archive',
    gz: 'archive',
    tar: 'archive',
    mp4: 'video',
    mov: 'video',
    avi: 'video',
    webm: 'video',
    mp3: 'audio',
    wav: 'audio',
    ogg: 'audio',
    m4a: 'audio',
    txt: 'text',
    md: 'text',
    log: 'text',
    json: 'text',
    xml: 'text',
}

/** Вид запису архіву для іконки — за розширенням (вмісту ми не читаємо). */
export const archiveEntryKind = (row: Pick<DiskArchiveRow, 'name' | 'dir'>): DiskFileKind => {
    if (row.dir) return 'folder'

    const dot = row.name.lastIndexOf('.')

    return (dot > 0 ? KIND_BY_EXTENSION[row.name.slice(dot + 1).toLowerCase()] : undefined) ?? 'other'
}
